import React, { useState } from 'react'
import { Plus, MessageCircle, Cloud, Volume2, Grid3X3, Rows, Minus, Palette } from 'lucide-react'
import { ComicTemplate, COMIC_TEMPLATES, ImageAsset } from '../types'

interface SidebarProps {
  currentTemplate: ComicTemplate
  panelCount: number
  selectedPanel?: string | null
  panels?: any[]
  onTemplateChange: (template: ComicTemplate) => void
  onAddDialog: (type: 'speech' | 'thought' | 'shout' | 'explode') => void
  onImageUpload: (imageData: string, file: File) => void
  onAddPanel: () => void
  onRemovePanel: () => void
  imageAssets: ImageAsset[]
  onAddImageAsset: (file: File, dataUrl: string) => void
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentTemplate,
  panelCount,
  selectedPanel,
  panels,
  onTemplateChange,
  onAddDialog,
  onImageUpload: _onImageUpload,
  onAddPanel,
  onRemovePanel,
  imageAssets: _imageAssets,
  onAddImageAsset: _onAddImageAsset
}) => {
  const [activeSection, setActiveSection] = useState<'templates' | 'panels' | 'dialogs' | 'background'>('panels')
  const [canvasBgColor, setCanvasBgColor] = useState('#ffffff')

  const sectionButtons = [
    { id: 'templates', label: '模板', icon: Grid3X3 },
    { id: 'panels', label: '面板', icon: Rows },
    { id: 'dialogs', label: '对话', icon: MessageCircle },
    { id: 'background', label: '背景', icon: Palette }
  ]

  const dialogTypes = [
    { type: 'speech' as const, label: '普通对话', icon: MessageCircle, color: 'bg-blue-500' },
    { type: 'thought' as const, label: '思考气泡', icon: Cloud, color: 'bg-purple-500' },
    { type: 'shout' as const, label: '喊叫气泡', icon: Volume2, color: 'bg-red-500' },
    { type: 'explode' as const, label: '爆炸气泡', icon: Palette, color: 'bg-yellow-400 text-yellow-900' }
  ]

  // const layoutTypes = [
  //   { type: 'vertical' as const, label: '垂直布局', icon: Rows },
  //   { type: 'grid' as const, label: '网格布局', icon: Grid3X3 },
  //   { type: 'horizontal' as const, label: '水平布局', icon: Columns }
  // ]

  // 上传图片后加入素材库
  // const handleImageUpload = (dataUrl: string, file: File) => {
  //   onAddImageAsset(file, dataUrl)
  // }

  return (
    <div className="w-80 bg-white/90 shadow-xl border-r border-gray-200 flex flex-col h-full overflow-hidden flex-shrink-0">
      {/* 当前工作面板提示 */}
      {selectedPanel && panels && (
        <div className="px-4 py-3 bg-blue-500 text-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-white rounded-full"></div>
            <div>
              <div className="text-sm font-medium">当前工作面板</div>
              <div className="text-xs opacity-90">
                面板 {panels.findIndex(p => p.id === selectedPanel) + 1} / {panels.length}
              </div>
            </div>
          </div>
          <div className="text-xs mt-1 opacity-80">
            💡 添加的气泡将自动放入此面板
          </div>
        </div>
      )}

      <div className="p-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex space-x-2 mb-4">
          {sectionButtons.map(btn => {
            const Icon = btn.icon
            return (
              <button
                key={btn.id}
                onClick={() => setActiveSection(btn.id as any)}
                className={`flex-1 flex flex-col items-center py-2 rounded-lg transition-colors ${activeSection === btn.id ? 'bg-primary-100 text-primary-600' : 'hover:bg-gray-100 text-gray-600'}`}
              >
                <Icon size={20} />
                <span className="text-xs mt-1">{btn.label}</span>
              </button>
            )
          })}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {activeSection === 'templates' && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">选择模板</h3>
            <div className="space-y-2">
              {COMIC_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => onTemplateChange(template)}
                  className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                    currentTemplate.id === template.id
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium">{template.name}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {template.panels.length}个面板
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'panels' && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">面板管理</h3>
            
            {/* 模板信息提示 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                <div className="text-xs text-blue-700">
                  <div className="font-medium mb-1">当前模板</div>
                  <div>
                    {currentTemplate.name} - {currentTemplate.type === 'single' ? '单排竖版布局' : '双排网格布局'}
                  </div>
                </div>
              </div>
            </div>
            
            {/* 面板数量控制 */}
            <div className="glass-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">面板数量</span>
                <span className="text-xs text-gray-500">当前: {panelCount}个</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onRemovePanel}
                  disabled={panelCount <= 1}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                    panelCount <= 1 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                  title="删除面板"
                >
                  <Minus size={16} />
                </button>
                <div className="flex-1 text-center">
                  <div className="text-lg font-semibold text-gray-800">{panelCount}</div>
                  <div className="text-xs text-gray-500">面板</div>
                </div>
                <button
                  onClick={onAddPanel}
                  disabled={panelCount >= 12}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                    panelCount >= 12 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                  title="添加面板"
                >
                  <Plus size={16} />
                </button>
              </div>
              <div className="text-xs text-gray-500 text-center">
                支持 1-12 个面板
              </div>
            </div>
          </div>
        )}

        {activeSection === 'dialogs' && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">添加对话气泡</h3>
              <p className="text-xs text-gray-500">选择合适的气泡类型来表达不同的情绪</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {dialogTypes.map((dialog) => {
                const Icon = dialog.icon
                return (
                  <button
                    key={dialog.type}
                    onClick={() => onAddDialog(dialog.type)}
                    className={`flex flex-col items-center justify-center p-4 rounded-xl text-white transition-all hover:scale-105 hover:shadow-lg ${dialog.color} group`}
                  >
                    <Icon size={24} className="mb-2 group-hover:scale-110 transition-transform" />
                    <span className="text-sm font-medium text-center leading-tight">{dialog.label}</span>
                  </button>
                )
              })}
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                气泡样式预览
              </h4>
              <div className="space-y-3 text-xs text-gray-600">
                <div className="flex items-center space-x-3 p-2 bg-white/60 rounded-lg">
                  <div className="w-6 h-6 bg-white border-2 border-gray-800 rounded-xl flex-shrink-0"></div>
                  <div>
                    <div className="font-medium text-gray-800">普通对话</div>
                    <div className="text-gray-500">圆角矩形，适合日常对话</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-white/60 rounded-lg">
                  <div className="w-6 h-6 bg-blue-50 border-2 border-dashed border-blue-400 rounded-full flex-shrink-0"></div>
                  <div>
                    <div className="font-medium text-gray-800">思考气泡</div>
                    <div className="text-gray-500">圆形虚线，表达内心想法</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-white/60 rounded-lg">
                  <div className="w-6 h-6 bg-red-50 border-4 border-red-600 rounded-sm flex-shrink-0"></div>
                  <div>
                    <div className="font-medium text-gray-800">喊叫气泡</div>
                    <div className="text-gray-500">方形粗边，表达强烈情绪</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-2 bg-white/60 rounded-lg">
                  <div className="w-6 h-6 bg-yellow-100 border-4 border-yellow-400 flex-shrink-0" style={{clipPath: 'polygon(10% 50%,0% 0%,25% 15%,33% 0%,50% 15%,67% 0%,75% 15%,100% 0%,90% 50%,100% 100%,75% 85%,67% 100%,50% 85%,33% 100%,25% 85%,0% 100%)'}}></div>
                  <div>
                    <div className="font-medium text-gray-800">爆炸气泡</div>
                    <div className="text-gray-500">锯齿形状，情绪爆发专用</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeSection === 'background' && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700">背景填充</h3>
            <div className="flex items-center space-x-4">
              <input
                type="color"
                value={canvasBgColor}
                onChange={e => {
                  setCanvasBgColor(e.target.value)
                  if (typeof window !== 'undefined' && window.dispatchEvent) {
                    window.dispatchEvent(new CustomEvent('canvas-bg-color', { detail: e.target.value }))
                  }
                }}
                className="w-10 h-10 rounded border shadow"
                aria-label="选择画布背景色"
              />
              <span className="text-sm font-mono">{canvasBgColor}</span>
            </div>
            <p className="text-xs text-gray-500">可实时调整整个条漫画布的背景颜色</p>
          </div>
        )}
      </div>
    </div>
  )
} 